/**
 * usePipelineInputs — the store-backed inputs for lib/pipelineHealth.ts.
 *
 * One hook, two consumers: the Pipeline Health report and the item list's
 * `pipelineStage` field. Both read the same sets built from the same stores,
 * so a stage count on the dashboard equals the row count of the list it opens.
 *
 * Priority lists are not hydrated by Layout (they are loaded per cycle or per
 * track on the pages that own them). "Committed" needs all of them, so the
 * first consumer to mount asks the store once. Sessions and pools are
 * hydrated by Layout; the asks here are a fallback for a cold store, following
 * decisions/useDecisionRelatedNames.ts.
 */

import { useEffect, useMemo } from 'react';
import { useAttributesStore } from '../../stores/prioritization/attributesStore';
import { useItemPoolsStore } from '../../stores/prioritization/itemPoolsStore';
import { usePrioritySetStore } from '../../stores/prioritization/prioritySetStore';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { useTriageStore } from '../../stores/prioritization/triageStore';
import { isItemReadyForPrioritization } from '../../lib/itemReadiness';
import { buildPipelineInputs, type PipelineInputs, type PipelineItem } from '../../lib/pipelineHealth';
import type { Candidate } from '../../types/prioritization/lifecycle';

/** Asked at most once per app session, so an empty workspace is not re-queried
    on every mount. Module-level on purpose: the store has no "fetched" flag. */
let prioritySetsRequested = false;

export function usePipelineInputs(): PipelineInputs {
  const assessments = useTriageStore((s) => s.assessments);
  const sessions = useSessionStore((s) => s.sessions);
  const itemPools = useItemPoolsStore((s) => s.itemPools);
  const prioritySets = usePrioritySetStore((s) => s.prioritySets);
  const attributes = useAttributesStore((s) => s.attributes);

  useEffect(() => {
    if (!prioritySetsRequested && prioritySets.length === 0 && !usePrioritySetStore.getState().isLoading) {
      prioritySetsRequested = true;
      void usePrioritySetStore.getState().fetchFromSupabase();
    }
    if (sessions.length === 0) void useSessionStore.getState().fetchFromSupabase?.();
    if (itemPools.length === 0) void useItemPoolsStore.getState().fetchFromSupabase?.();
    // Deliberately runs once: the store lengths are read, not watched, so a
    // workspace with nothing in a store is asked once, not on every change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requiredAttributes = useMemo(
    () => attributes.filter((a) => a.required).map((a) => ({ key: a.key, label: a.label, required: a.required })),
    [attributes],
  );

  return useMemo(
    () =>
      buildPipelineInputs({
        assessments,
        sessions,
        itemPools,
        prioritySets,
        isReady: (item: PipelineItem) => isItemReadyForPrioritization(item as Candidate, requiredAttributes),
      }),
    [assessments, sessions, itemPools, prioritySets, requiredAttributes],
  );
}
